import { useState, useEffect, useCallback } from 'react'

/**
 * Tracks whether the browser is online.
 * Uses navigator.onLine + online/offline window events for real-time detection.
 *
 * Usage:
 *   const isOnline = useOnlineStatus()
 *   if (!isOnline) return <OfflineBanner />
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )

  const handleOnline = useCallback(() => setIsOnline(true), [])
  const handleOffline = useCallback(() => setIsOnline(false), [])

  useEffect(() => {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [handleOnline, handleOffline])

  return isOnline
}
